/* =========================================================
   ZWC Sidebar Release + Smart Sticky

   SMART STICKY MODE (default — data-smart-sticky != "false"):
   The sidebar scrolls with the page in BOTH directions:
   – Scrolling DOWN: it travels with the page until its bottom
     edge reaches the viewport bottom (minus the gap), then pins.
   – Scrolling UP: it immediately travels back down with the
     page until its top edge reaches --zwc-sticky-top, then pins.
   End-of-section release happens automatically via the sticky
   container clamp. Desktop only (>= 990px); the mobile drawer
   is never touched.

   LEGACY RELEASE MODE (data-smart-sticky == "false"):
   Original behaviour, unchanged:

   MODE A (inner scroll) — if the pinned panel has its own
   internal scrollbar, page scroll inside the release zone is
   forwarded 1:1 into the panel's scrollTop, so the filters
   scroll down with the page and reach their bottom exactly
   as the section ends. Scrolling the page back up scrolls
   the filters back up the same way.

   MODE B (translate fallback) — if instead the pinned sidebar
   overhangs below the viewport with no inner scrollbar, it is
   shifted up just enough to reveal its hidden bottom, then
   re-pins. Never leaves the screen.

   Re-syncs on zwc:rerender, resize and container size changes.
========================================================= */
(function () {
  'use strict';

  /* Sidebar-beside-grid layout only — never runs in mobile drawer mode */
  var DESKTOP_MQ = '(min-width: 990px)';

  function toPx(v) {
    var n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }

  /* =========================================================
     SMART STICKY MODE
  ========================================================= */
  function initSmartSticky(root) {
    var mq = window.matchMedia(DESKTOP_MQ);

    var sidebar = null;
    var container = null;
    var lastY = window.scrollY || window.pageYOffset;
    var dir = 'down';
    var pinnedTop = false;
    var ticking = false;
    var engaged = false;

    function grab() {
      sidebar = root.querySelector('[data-zwc-sidebar]');
      container = sidebar ? sidebar.parentElement : null;
      pinnedTop = false;
      engaged = false;
    }

    function rootVar(name) {
      return toPx(getComputedStyle(root).getPropertyValue(name));
    }

    function clearInline() {
      if (!sidebar) return;
      sidebar.style.position = '';
      sidebar.style.top = '';
      sidebar.style.marginTop = '';
      sidebar.style.alignSelf = '';
      sidebar.style.maxHeight = '';
      sidebar.style.height = '';
      var panel = sidebar.querySelector('.zwc-filter-panel');
      var body = sidebar.querySelector('.zwc-filter-body');
      if (panel) { panel.style.maxHeight = ''; panel.style.height = ''; }
      if (body) { body.style.maxHeight = ''; body.style.overflow = ''; }
      engaged = false;
      pinnedTop = false;
    }

    function engage() {
      /* Full natural height so the sidebar can travel + pin;
         the page scroll replaces the inner filter scrollbar on desktop. */
      sidebar.style.position = 'sticky';
      sidebar.style.alignSelf = 'flex-start';
      sidebar.style.maxHeight = 'none';
      sidebar.style.height = 'auto';
      var panel = sidebar.querySelector('.zwc-filter-panel');
      var body = sidebar.querySelector('.zwc-filter-body');
      if (panel) { panel.style.maxHeight = 'none'; panel.style.height = 'auto'; }
      if (body) { body.style.maxHeight = 'none'; body.style.overflow = 'visible'; }
      engaged = true;
    }

    function update() {
      ticking = false;
      if (!sidebar || !sidebar.isConnected) grab();
      if (!sidebar || !container) return;

      /* Mobile / drawer breakpoint — hands the sidebar back to the theme CSS */
      if (!mq.matches) {
        if (engaged) clearInline();
        lastY = window.scrollY || window.pageYOffset;
        return;
      }
      if (!engaged) engage();

      var y = window.scrollY || window.pageYOffset;
      var newDir = y > lastY ? 'down' : (y < lastY ? 'up' : dir);
      lastY = y;

      var winH = window.innerHeight || document.documentElement.clientHeight;
      var tGap = rootVar('--zwc-sticky-top');
      var bGap = rootVar('--zwc-release-gap-d');
      var sbH = sidebar.offsetHeight;

      /* Sidebar fits in the viewport → plain top-sticky */
      if (sbH + tGap + bGap <= winH) {
        sidebar.style.top = tGap + 'px';
        if (sidebar.style.marginTop) sidebar.style.marginTop = '';
        pinnedTop = true;
        dir = newDir;
        return;
      }

      /* Negative top → the browser pins the sidebar exactly when its
         bottom edge rises `bGap` above the viewport bottom. */
      var negTop = winH - sbH - bGap;

      var sbRect = sidebar.getBoundingClientRect();
      var ctRect = container.getBoundingClientRect();

      if (newDir !== dir) {
        /* Direction changed → freeze the sidebar at its current spot
           inside the container so it travels smoothly from here. */
        var offset = sbRect.top - ctRect.top;
        var maxOffset = container.offsetHeight - sbH;
        if (maxOffset < 0) maxOffset = 0;
        offset = Math.max(0, Math.min(offset, maxOffset));
        sidebar.style.marginTop = offset + 'px';
        pinnedTop = false;
        dir = newDir;
      }

      if (dir === 'down') {
        /* Travels with the page, then pins automatically at the bottom */
        sidebar.style.top = negTop + 'px';
        pinnedTop = false;
      } else {
        if (pinnedTop) {
          sidebar.style.top = tGap + 'px';
        } else if (sbRect.top >= tGap - 1) {
          /* Top edge reached the sticky offset → pin to top seamlessly */
          sidebar.style.top = tGap + 'px';
          sidebar.style.marginTop = '';
          pinnedTop = true;
        } else {
          /* Still travelling upward with the page */
          sidebar.style.top = negTop + 'px';
        }
      }
    }

    function requestTick() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    function reset() {
      if (sidebar) sidebar.style.marginTop = '';
      pinnedTop = false;
      requestTick();
    }

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', reset);
    window.addEventListener('load', reset);

    /* Filter/sort AJAX swaps DOM — re-grab and re-evaluate */
    document.addEventListener('zwc:rerender', function () {
      grab();
      requestTick();
    });

    if (mq.addEventListener) mq.addEventListener('change', reset);
    else if (mq.addListener) mq.addListener(reset);

    grab();
    requestTick();
  }

  /* =========================================================
     LEGACY RELEASE MODE (original logic, unchanged)
  ========================================================= */
  function initRelease(root) {
    if (root.dataset.releaseEnabled === 'false') return;

    var sidebar = root.querySelector('[data-zwc-sidebar]');
    var container = root.querySelector('.zwc-container');
    if (!sidebar || !container) return;

    var form = sidebar.querySelector('.zwc-filter-form');
    var panel = sidebar.querySelector('.zwc-filter-panel');
    var body = sidebar.querySelector('.zwc-filter-body');
    var mq = window.matchMedia(DESKTOP_MQ);

    var target = null;              /* pinned element (for MODE B translate)   */
    var scroller = null;            /* inner scrollable element (for MODE A)   */
    var containerBottomDoc = 0;     /* cached: section-container bottom (doc)  */
    var viewportH = 0;              /* cached viewport height                  */
    var gapCached = 0;              /* cached gap setting                      */
    var maxShift = 0;               /* MODE B: hidden overhang — hard cap      */
    var translateReleasePoint = Infinity; /* MODE B trigger scrollY            */
    var currentTranslate = 0;
    var lastScrollY = window.scrollY || window.pageYOffset;
    var ticking = false;

    function readGap() {
      var cs = getComputedStyle(root);
      var raw = mq.matches
        ? cs.getPropertyValue('--zwc-release-gap-d')
        : cs.getPropertyValue('--zwc-release-gap-m');
      return toPx(raw);
    }

    /* The sticky/fixed element could be the aside, the form, or the panel
       depending on the theme CSS — detect it instead of assuming. */
    function findTarget() {
      var candidates = [sidebar, form, panel];
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        if (!el) continue;
        var pos = getComputedStyle(el).position;
        if (pos === 'sticky' || pos === 'fixed') return el;
      }
      return null;
    }

    /* Find the element that actually owns the inner scrollbar */
    function findScroller() {
      var candidates = [body, panel, form, sidebar];
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        if (!el) continue;
        var oy = getComputedStyle(el).overflowY;
        if (oy !== 'auto' && oy !== 'scroll' && oy !== 'overlay') continue;
        if (el.scrollHeight - el.clientHeight > 4) return el;
      }
      return null;
    }

    function clearTransform(el) {
      if (!el) return;
      el.style.transform = '';
      el.style.willChange = '';
    }

    function resetTranslate() {
      if (currentTranslate !== 0) {
        clearTransform(target);
        currentTranslate = 0;
      }
    }

    /* Re-resolve nodes if an AJAX rerender replaced the sidebar DOM */
    function resolveNodes() {
      if (!sidebar || !sidebar.isConnected) {
        sidebar = root.querySelector('[data-zwc-sidebar]');
        form = sidebar ? sidebar.querySelector('.zwc-filter-form') : null;
        panel = sidebar ? sidebar.querySelector('.zwc-filter-panel') : null;
        body = sidebar ? sidebar.querySelector('.zwc-filter-body') : null;
      }
      if (!container || !container.isConnected) {
        container = root.querySelector('.zwc-container');
      }
      return !!(sidebar && container);
    }

    function measure() {
      if (!resolveNodes()) {
        target = null; scroller = null;
        translateReleasePoint = Infinity; maxShift = 0;
        return;
      }

      var prevTarget = target;
      target = findTarget();
      if (prevTarget && prevTarget !== target) clearTransform(prevTarget);

      scroller = findScroller();

      gapCached = readGap();
      viewportH = window.innerHeight || document.documentElement.clientHeight;

      var scrollY = window.scrollY || window.pageYOffset;
      containerBottomDoc = container.getBoundingClientRect().bottom + scrollY;

      /* MODE B geometry (only meaningful when there's no inner scroller) */
      if (target) {
        var prev = target.style.transform;
        target.style.transform = '';
        var rect = target.getBoundingClientRect();
        var pinnedTopRaw = getComputedStyle(target).top;
        var pinnedTop = (pinnedTopRaw && pinnedTopRaw !== 'auto') ? toPx(pinnedTopRaw) : rect.top;
        maxShift = Math.max(0, pinnedTop + rect.height + gapCached - viewportH);
        translateReleasePoint = containerBottomDoc - gapCached - rect.height - pinnedTop;
        target.style.transform = prev;
      } else {
        maxShift = 0;
        translateReleasePoint = Infinity;
      }

      lastScrollY = scrollY;
    }

    function apply() {
      ticking = false;

      var scrollY = window.scrollY || window.pageYOffset;
      var prevY = lastScrollY;
      lastScrollY = scrollY;

      if (!mq.matches) { resetTranslate(); return; }

      /* ---------- MODE A: sync the inner scrollbar with the page ---------- */
      if (scroller && scroller.isConnected) {
        var innerMax = scroller.scrollHeight - scroller.clientHeight;
        if (innerMax > 4) {
          resetTranslate(); /* never mix modes */

          /* scrollY at which the section's bottom edge reaches the viewport
             bottom (minus the configured gap) — inner scroll must finish here */
          var endScrollY = containerBottomDoc - viewportH - gapCached;
          /* start syncing exactly innerMax px of page scroll before that,
             so page-end and filter-end line up 1:1 */
          var releasePoint = Math.max(0, endScrollY - innerMax);

          if (scrollY > releasePoint || prevY > releasePoint) {
            var delta = scrollY - prevY;
            if (delta !== 0) {
              var next = scroller.scrollTop + delta;
              if (next < 0) next = 0;
              if (next > innerMax) next = innerMax;
              if (next !== scroller.scrollTop) scroller.scrollTop = next;
            }
          }
          return;
        }
      }

      /* ---------- MODE B: reveal hidden overhang via clamped translate ---------- */
      if (!target || maxShift <= 0) { resetTranslate(); return; }

      var overshoot = scrollY - translateReleasePoint;
      var shift = Math.min(Math.max(overshoot, 0), maxShift);
      var translate = -Math.round(shift);

      if (translate === currentTranslate) return;
      currentTranslate = translate;

      if (translate === 0) {
        clearTransform(target);
      } else {
        target.style.willChange = 'transform';
        target.style.transform = 'translateY(' + translate + 'px)';
      }
    }

    function requestApply() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }

    /* Double rAF so layout settles first (same pattern as zwc-scroll-guard) */
    function remeasure() {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          measure();
          requestApply();
        });
      });
    }

    window.addEventListener('scroll', requestApply, { passive: true });
    window.addEventListener('resize', remeasure);
    window.addEventListener('load', remeasure);
    document.addEventListener('zwc:rerender', remeasure);

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(remeasure);
      ro.observe(container);
      ro.observe(sidebar);
    }

    if (mq.addEventListener) mq.addEventListener('change', remeasure);
    else if (mq.addListener) mq.addListener(remeasure);

    remeasure();
  }

  /* =========================================================
     BOOT — pick one mode per section root
  ========================================================= */
  function initRoot(root) {
    if (root.dataset.smartSticky !== 'false') {
      initSmartSticky(root);
    } else {
      initRelease(root);
    }
  }

  function boot() {
    document.querySelectorAll('[data-zwc-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();